import {
  CalendarRange,
  DoorOpen,
  Eye,
  FileSpreadsheet,
  Grid3x3,
  LayoutDashboard,
  LogOut,
  Menu,
  PanelLeftClose,
  RefreshCw,
  ScanLine,
  ContactRound,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { ROLE_LABELS, type RoleName } from '@es/shared';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';
import { Logotipo } from './Marca';
import { useAuth } from '../lib/auth';
import { useContasAguardando } from '../lib/contasAguardando';
import { resolverVisao } from '../lib/visao';

function rotuloPapel(role: string): string {
  return ROLE_LABELS[role as RoleName] ?? role;
}

const PERFIS_PARA_VISUALIZAR: readonly RoleName[] = [
  'ADMINISTRADOR',
  'RH',
  'GESTOR',
  'COLABORADOR',
];

function SeletorPerfil({
  valor,
  aoAlterar,
  compacto = false,
}: {
  valor: RoleName;
  aoAlterar: (role: RoleName) => void;
  compacto?: boolean;
}): JSX.Element {
  return (
    <label className={compacto ? 'relative' : 'block'}>
      <span
        className={
          compacto
            ? 'sr-only'
            : 'mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45'
        }
      >
        {!compacto ? <Eye className="h-3.5 w-3.5" aria-hidden="true" /> : null}
        Visualizar como
      </span>
      <select
        value={valor}
        onChange={(event) => aoAlterar(event.target.value as RoleName)}
        className={
          compacto
            ? 'h-10 max-w-32 rounded-xl border border-white/10 bg-white/[0.08] px-2 text-xs font-semibold text-white outline-none focus:border-white/35'
            : 'h-10 w-full rounded-xl border border-white/10 bg-[#292927] px-3 text-xs font-semibold text-white outline-none transition focus:border-white/35 focus:ring-2 focus:ring-white/10'
        }
        aria-label="Visualizar plataforma como"
      >
        {PERFIS_PARA_VISUALIZAR.map((role) => (
          <option key={role} value={role} className="bg-tinta-preto text-white">
            {ROLE_LABELS[role]}
          </option>
        ))}
      </select>
    </label>
  );
}

interface ItemMenu {
  para: string;
  rotulo: string;
  Icone: typeof LayoutDashboard;
  /** Permissão exigida. O backend valida de novo — aqui é só para não mostrar o inalcançável. */
  permissao?: string;
  /** Pendências que pedem ação de quem vê o item; zero não mostra nada. */
  pendencias?: number;
  /** Escondido do colaborador, que usa apenas "Minha escala". */
  foraDoColaborador?: boolean;
}

function ContadorPendencias({
  total,
  invertido,
}: {
  total: number;
  invertido: boolean;
}): JSX.Element | null {
  if (total <= 0) return null;
  return (
    <span
      className={`ml-auto rounded-full px-1.5 text-2xs font-semibold leading-5 ${
        invertido ? 'bg-tinta-preto text-white' : 'bg-white text-tinta-preto'
      }`}
      title={`${total} conta(s) aguardando liberação`}
    >
      {total}
      <span className="sr-only"> conta(s) aguardando liberação</span>
    </span>
  );
}

export function Layout(): JSX.Element {
  const {
    user,
    sair,
    pode,
    podeEspelharPerfis = false,
    perfilVisualizado = 'ADMINISTRADOR',
    alterarPerfilVisualizado = () => undefined,
  } = useAuth();
  const queryClient = useQueryClient();
  const [menuAberto, setMenuAberto] = useState(true);
  const visao = resolverVisao(pode);
  const contasAguardando = useContasAguardando();
  const aplicarPerfilVisualizado = (role: RoleName): void => {
    if (role === perfilVisualizado) return;
    // Não reutiliza na simulação dados consultados sob outra visão.
    queryClient.clear();
    alterarPerfilVisualizado(role);
  };

  const menu: ItemMenu[] = [
    { para: '/', rotulo: 'Painel', Icone: LayoutDashboard, foraDoColaborador: true },
    {
      para: '/escala',
      rotulo: visao.colaborador ? 'Minha escala' : 'Escala semanal',
      Icone: CalendarRange,
      permissao: 'escala:ver_propria',
    },
    {
      para: '/minha-equipe',
      rotulo: 'Time',
      Icone: Users,
      permissao: 'escala:ver_propria',
      foraDoColaborador: true,
    },
    {
      para: '/presenca',
      rotulo: 'Presença',
      Icone: ScanLine,
      permissao: 'escala:ver_propria',
    },
    {
      // Mesma página para todos; sem o mapa, ela mostra só as salas de reunião.
      para: '/mesas',
      rotulo: visao.mapa ? 'Mesas e mapa' : 'Salas de reunião',
      Icone: visao.mapa ? Grid3x3 : DoorOpen,
      permissao: 'sala:reservar',
    },
    {
      para: '/swile',
      rotulo: 'Fechamento Swile',
      Icone: FileSpreadsheet,
      permissao: 'beneficio:calcular',
    },
    {
      para: '/convenia',
      rotulo: 'Convenia',
      Icone: RefreshCw,
      permissao: 'integracoes:sincronizar',
    },
    {
      para: '/colaboradores',
      rotulo: 'Colaboradores',
      Icone: ContactRound,
      permissao: 'colaborador:ver',
      pendencias: contasAguardando,
    },
  ];

  const itens = menu.filter(
    (item) =>
      (!item.permissao || pode(item.permissao)) && !(visao.colaborador && item.foraDoColaborador),
  );

  return (
    <div className="flex min-h-screen">
      <aside
        className={`sticky top-0 hidden h-screen shrink-0 flex-col overflow-hidden bg-tinta-preto text-white shadow-[8px_0_32px_rgba(29,29,27,0.12)] transition-[width,transform] duration-200 md:flex ${menuAberto ? 'w-64' : 'w-0 -translate-x-full'}`}
        aria-hidden={!menuAberto}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-5">
          <Logotipo invertido />
          <button
            type="button"
            onClick={() => setMenuAberto(false)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/45 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Ocultar menu lateral"
            title="Ocultar menu lateral"
          >
            <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <nav
          className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-5"
          aria-label="Navegação principal"
        >
          <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">
            Operação
          </p>
          {itens.map(({ para, rotulo, Icone, pendencias }) => (
            <NavLink
              key={para}
              to={para}
              end={para === '/'}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all ${
                  isActive
                    ? 'bg-white font-semibold text-tinta-preto shadow-lg shadow-black/20'
                    : 'text-white/65 hover:translate-x-0.5 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icone
                    className={`h-4 w-4 shrink-0 ${isActive ? 'text-tinta-preto' : 'text-white/45 group-hover:text-white'}`}
                    aria-hidden="true"
                  />
                  {rotulo}
                  <ContadorPendencias total={pendencias ?? 0} invertido={isActive} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 bg-white/[0.03] p-4">
          {podeEspelharPerfis ? (
            <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <SeletorPerfil valor={perfilVisualizado} aoAlterar={aplicarPerfilVisualizado} />
              <p className="mt-2 text-[10px] leading-relaxed text-white/35">
                Simula menus e permissões visuais sem alterar seu acesso de administrador.
              </p>
            </div>
          ) : null}
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-xs font-bold text-white">
              {user?.displayName?.trim().charAt(0).toUpperCase() ?? 'E'}
            </div>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-white">{user?.displayName}</p>
              <p className="truncate text-2xs text-white/45">{user?.email}</p>
              <p className="mt-0.5 truncate text-[10px] uppercase tracking-wide text-white/35">
                {user?.roles.map(rotuloPapel).join(' · ')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={sair}
            className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold text-white/50 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sair
          </button>
        </div>
      </aside>

      {!menuAberto ? (
        <button
          type="button"
          onClick={() => setMenuAberto(true)}
          className="fixed left-4 top-4 z-40 hidden h-10 w-10 items-center justify-center rounded-xl border border-superficie-borda bg-white text-tinta-preto shadow-flutuante transition hover:-translate-y-px md:flex"
          aria-label="Abrir menu lateral"
          title="Abrir menu lateral"
        >
          <Menu className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {/* Navegação compacta para telas estreitas. */}
      <nav
        className="fixed inset-x-0 bottom-0 z-20 flex snap-x overflow-x-auto border-t border-white/10 bg-tinta-preto/95 px-2 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden"
        aria-label="Navegação principal"
      >
        {itens.map(({ para, rotulo, Icone, pendencias }) => (
          <NavLink
            key={para}
            to={para}
            end={para === '/'}
            className={({ isActive }) =>
              `relative flex min-w-[4.75rem] flex-none snap-start flex-col items-center gap-0.5 px-2 py-2 text-2xs ${
                isActive ? 'font-semibold text-white' : 'text-white/45'
              }`
            }
          >
            <Icone className="h-4 w-4" aria-hidden="true" />
            <span className="truncate">{rotulo.split(' ')[0]}</span>
            {pendencias ? (
              <span className="absolute right-1/4 top-1">
                <ContadorPendencias total={pendencias} invertido={false} />
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-tinta-preto px-4 py-3 text-white shadow-lg md:hidden">
          <Logotipo invertido />
          <div className="flex items-center gap-2">
            {podeEspelharPerfis ? (
              <SeletorPerfil
                valor={perfilVisualizado}
                aoAlterar={aplicarPerfilVisualizado}
                compacto
              />
            ) : null}
            <div className="hidden text-right min-[430px]:block">
              <p className="max-w-40 truncate text-xs font-semibold">{user?.displayName}</p>
              <p className="max-w-40 truncate text-[10px] text-white/45">
                {user?.roles.map(rotuloPapel).join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={sair}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] text-white/70 transition-colors hover:bg-white/15 hover:text-white"
              aria-label="Sair do sistema"
              title="Sair"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </header>

        {podeEspelharPerfis && perfilVisualizado !== 'ADMINISTRADOR' ? (
          <div
            className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-950 sm:px-6"
            role="status"
          >
            <span className="flex items-center gap-2">
              <Eye className="h-4 w-4" aria-hidden="true" />
              Visualização simulada de <strong>{ROLE_LABELS[perfilVisualizado]}</strong>
            </span>
            <button
              type="button"
              onClick={() => aplicarPerfilVisualizado('ADMINISTRADOR')}
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-950 transition hover:bg-amber-100"
            >
              Voltar ao administrador
            </button>
          </div>
        ) : null}

        <main className="min-w-0 pb-16 md:pb-0">
          <Outlet key={perfilVisualizado} />
        </main>
      </div>
    </div>
  );
}

export function Pagina({
  titulo,
  descricao,
  acoes,
  children,
}: {
  titulo: string;
  descricao?: string;
  acoes?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
      <header className="relative mb-6 overflow-hidden rounded-2xl bg-tinta-preto px-5 py-6 text-white shadow-flutuante sm:px-7 sm:py-7">
        <div className="absolute inset-y-0 left-0 w-1 bg-white/80" aria-hidden="true" />
        <div
          className="pointer-events-none absolute -right-12 -top-24 h-56 w-56 rounded-full border-[42px] border-white/[0.06]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-1/2 h-40 w-40 rounded-full border-[24px] border-white/[0.035]"
          aria-hidden="true"
        />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">
              Eckermann & Santos · Workplace
            </p>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              {titulo}
            </h1>
            {descricao ? (
              <p className="mt-1.5 max-w-3xl text-sm text-white/60">{descricao}</p>
            ) : null}
          </div>
          {acoes ? (
            <div className="relative flex flex-wrap items-center gap-2 rounded-xl bg-white/10 p-1">
              {acoes}
            </div>
          ) : null}
        </div>
      </header>
      <div className="space-y-5">{children}</div>
    </div>
  );
}
